import React, { useCallback, useEffect } from 'react';
import { observer } from 'mobx-react-lite';
import { useInjection } from 'inversify-react';
import { ChatBubble } from './ChatBubble';
import { ChatWindow } from './ChatWindow';
import { ChatWidgetStore } from '../model/chatWidgetStore';
import { useChatContext } from '../hooks/useChatContext';
import { useAutoOpen } from '../hooks/useAutoOpen';

export const ChatWidget: React.FC = observer(() => {
  const store = useInjection(ChatWidgetStore);
  const context = useChatContext();

  const handleAutoOpen = useCallback(() => {
    store.setOpen(true);
  }, [store]);

  useAutoOpen(handleAutoOpen, context);

  useEffect(() => {
    if (store.isOpen && store.status === 'disconnected') {
      store.initConnection(context);
    }
  }, [store.isOpen, store.status, context, store]);

  const handleSendMessage = useCallback(
    (text: string) => {
      store.sendMessage(text);
    },
    [store]
  );

  const handleQuickReply = useCallback(
    (text: string) => {
      store.sendMessage(text);
    },
    [store]
  );

  const handleToggle = useCallback(() => {
    store.toggleOpen();
  }, [store]);

  const handleClose = useCallback(() => {
    store.setOpen(false);
  }, [store]);

  const handleNewChat = useCallback(() => {
    store.clearSession();
    store.initConnection(context);
  }, [store, context]);

  return (
    <>
      <ChatBubble isOpen={store.isOpen} unreadCount={store.unreadCount} onClick={handleToggle} />
      {store.isOpen && (
        <ChatWindow
          messages={store.messages}
          isTyping={store.isTyping}
          typingSender={store.typingSender}
          quickReplies={store.quickReplies}
          buttonsQuestion={store.buttonsQuestion}
          status={store.status}
          onSendMessage={handleSendMessage}
          onQuickReply={handleQuickReply}
          onClose={handleClose}
          onNewChat={handleNewChat}
          showResolvePrompt={store.showResolvePrompt}
          onResolveYes={() => store.confirmResolved()}
          onResolveNo={() => store.declineResolved()}
        />
      )}
    </>
  );
});
